import { datadogRum } from '@datadog/browser-rum';
import type { ApiError } from '../../interceptors/error-handler.interceptor';

export interface ApiErrorContext {
  endpoint: string;
  status: number;
  code: string;
  method?: string;
  [k: string]: string | number | undefined;
}

/**
 * Reports a failed API call to RUM as a custom error. No `isRumReady` guard on purpose:
 * the SDK buffers events fired before `init`. `endpoint` must come from `normalizeApiEndpoint`
 * (or an `ext:*` key): never put emails, tokens or full URLs in the context.
 */
export function reportApiError(ctx: ApiErrorContext): void {
  try {
    const err = new Error('api_error') as Error & { dd_fingerprint?: string };
    err.dd_fingerprint = `api_error:${ctx.method ?? ''}:${ctx.endpoint}:${ctx.status}`;
    datadogRum.addError(err, ctx);
  } catch {
    // Telemetry never breaks the flow.
  }
}

/** True for the plain `ApiError` object the HTTP interceptor throws (not for `Error` instances). */
export function isApiError(e: unknown): e is ApiError {
  const x = e as Partial<ApiError> | null | undefined;
  return !!x && typeof x.status === 'number' && typeof x.error === 'string' && !(e instanceof Error);
}
